import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { coverUrl, api } from "../lib/api";
import { Button } from "../components/ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { cart, remove } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [busyPaypal, setBusyPaypal] = React.useState(false);

  const onCheckout = async () => {
    if (!user) { navigate("/login?next=/cart"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/cart", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setBusy(false); }
  };

  const onPaypal = async () => {
    if (!user) { navigate("/login?next=/cart"); return; }
    setBusyPaypal(true);
    try {
      const { data } = await api.post("/checkout/paypal/create", { origin_url: window.location.origin });
      window.location.href = data.approve_url;
    } catch (e) { toast.error(e.response?.data?.detail || "PayPal checkout failed"); setBusyPaypal(false); }
  };

  return (
    <div className="container-page py-10" data-testid="cart-page">
      <h1 className="font-display text-4xl tracking-tight border-b border-border pb-3 mb-8">Your cart</h1>
      {!cart?.items?.length ? (
        <div className="text-center py-24 border border-dashed border-border rounded-sm">
          <div className="font-display text-3xl">Your cart is empty.</div>
          <div className="text-sm text-muted-foreground mt-2 italic">Find something good to read.</div>
          <Button asChild className="mt-6 rounded-sm" data-testid="browse-cta"><Link to="/browse">Browse catalogue</Link></Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 divide-y divide-border border-t border-b border-border">
            {cart.items.map((p) => (
              <div key={p.id} className="flex gap-4 py-4" data-testid={`cart-item-${p.id}`}>
                <Link to={`/product/${p.id}`} className="w-20 sm:w-24 aspect-[2/3] bg-muted overflow-hidden rounded-sm border border-border">
                  <img src={coverUrl(p)} alt={p.title} className="object-cover w-full h-full" />
                </Link>
                <div className="flex-1">
                  <Link to={`/product/${p.id}`} className="font-display text-lg hover:text-accent">{p.title}</Link>
                  <div className="text-xs text-muted-foreground italic">{p.subtitle}</div>
                  <div className="text-xs text-muted-foreground mt-1">{p.page_count} pages · {p.language}</div>
                  <button onClick={() => remove(p.id)} className="mt-2 text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid={`remove-${p.id}`}>
                    <X className="h-3 w-3" strokeWidth={1.5} /> Remove
                  </button>
                </div>
                <div className="text-base font-semibold">A${p.price.toFixed(2)}</div>
              </div>
            ))}
          </div>
          <aside>
            <div className="border border-border rounded-sm p-6 sticky top-32 bg-card" data-testid="cart-summary">
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Items</span><span>{cart.items.length}</span></div>
              <div className="flex items-center justify-between text-sm mt-2"><span className="text-muted-foreground">Subtotal</span><span>A${(cart.subtotal ?? cart.total).toFixed(2)}</span></div>
              {cart.discount_amount > 0 && (
                <div className="flex items-start justify-between text-sm mt-2 text-accent" data-testid="cart-discount">
                  <span className="italic leading-snug pr-2">
                    {cart.discount_label || "Welcome offer"}
                  </span>
                  <span className="whitespace-nowrap">−A${cart.discount_amount.toFixed(2)}</span>
                </div>
              )}
              {cart.items.length === 1 && (
                <p className="text-[11px] text-muted-foreground italic mt-3 leading-relaxed" data-testid="cart-welcome-hint">
                  Add a second title and the cheaper of the two becomes 50% off — welcome offer, no code needed.
                </p>
              )}
              <div className="border-t border-border my-4" />
              <div className="flex items-center justify-between text-lg font-semibold"><span>Total</span><span>A${cart.total.toFixed(2)}</span></div>
              <Button onClick={onCheckout} disabled={busy || busyPaypal} className="w-full mt-5 rounded-sm uppercase tracking-wide" data-testid="checkout-button">
                {busy ? "Redirecting…" : "Pay with card"}
              </Button>
              <Button onClick={onPaypal} disabled={busy || busyPaypal} variant="outline" className="w-full mt-2 rounded-sm uppercase tracking-wide border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white" data-testid="paypal-button">
                {busyPaypal ? "Redirecting…" : "Pay with PayPal"}
              </Button>
              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed italic">Secure checkout via Stripe or PayPal.</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
